import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Upload, Sparkles, Send, Check, X, ShieldCheck, ClipboardList } from "lucide-react";

const TABS = ["Overview", "Chronology", "Documents", "AI Studio", "Tasks", "Messages"];

export default function CaseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "staff_rn";
  const [tab, setTab] = useState("Overview");
  const [c, setC] = useState(null);
  const [chrono, setChrono] = useState([]);
  const [docs, setDocs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [ai, setAi] = useState([]);

  const loadAll = useCallback(async () => {
    const [a, b, d, t, m, r] = await Promise.all([
      api.get(`/cases/${id}`), api.get(`/cases/${id}/chronology`),
      api.get(`/cases/${id}/documents`), api.get(`/cases/${id}/tasks`),
      api.get(`/cases/${id}/messages`), api.get(`/cases/${id}/ai/runs`),
    ]);
    setC(a.data); setChrono(b.data); setDocs(d.data); setTasks(t.data); setMsgs(m.data); setAi(r.data);
  }, [id]);
  useEffect(() => { loadAll(); }, [loadAll]);

  if (!c) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="fade-in-up">
      <Link to="/portal/cases" className="inline-flex items-center gap-2 text-caption text-slate-500 hover:text-[#0A192F] mb-4" data-testid="back-to-cases"><ArrowLeft className="w-3.5 h-3.5" /> All Cases</Link>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-2">
        <div>
          <div className="text-caption text-[#B91C1C]">{c.case_number}</div>
          <h1 className="font-serif text-3xl font-bold text-[#0A192F]">{c.title}</h1>
          <div className="mt-2 flex gap-2 flex-wrap">
            <span className="badge-federal">{c.status}</span>
            {c.is_veteran && <span className="badge-gold">Veteran · {c.branch_of_service || "Unspecified"}</span>}
            <span className="badge-federal">{c.urgency}</span>
            <span className={`badge-federal ${c.conflict_check_status === "cleared" ? "!border-[#15803D] !text-[#15803D]" : "!border-[#B45309] !text-[#B45309]"}`}>Conflict: {c.conflict_check_status}</span>
          </div>
        </div>
        <div className="text-caption text-slate-500">Client: <span className="text-slate-800">{c.client_name}</span></div>
      </div>

      <div className="flex gap-1 border-b border-slate-200 mt-8 mb-6 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase().replace(/\s/g,"-")}`}
            className={`px-4 py-2.5 text-[12px] uppercase tracking-widest border-b-2 -mb-px ${tab === t ? "border-[#B91C1C] text-[#0A192F]" : "border-transparent text-slate-500 hover:text-[#0A192F]"}`}>{t}</button>
        ))}
      </div>

      <TabContent tab={tab} ctx={{ c, chrono, docs, ai, tasks, msgs, id, isStaff, loadAll, user }} />
    </div>
  );
}

function TabContent({ tab, ctx }) {
  switch (tab) {
    case "Overview": return <Overview c={ctx.c} isStaff={ctx.isStaff} onChange={ctx.loadAll} />;
    case "Chronology": return <Chronology events={ctx.chrono} caseId={ctx.id} onAdd={ctx.loadAll} />;
    case "Documents": return <Documents docs={ctx.docs} caseId={ctx.id} onChange={ctx.loadAll} />;
    case "AI Studio": return <AIStudio runs={ctx.ai} caseId={ctx.id} isStaff={ctx.isStaff} onChange={ctx.loadAll} />;
    case "Tasks": return <Tasks tasks={ctx.tasks} caseId={ctx.id} onChange={ctx.loadAll} />;
    case "Messages": return <Messages msgs={ctx.msgs} caseId={ctx.id} onChange={ctx.loadAll} me={ctx.user} />;
    default: return null;
  }
}

function Overview({ c, isStaff, onChange }) {
  const [f, setF] = useState({
    status: c.status, merit_review: c.merit_review || "", standards_of_care: c.standards_of_care || "",
    damages_analysis: c.damages_analysis || "", conflict_check_status: c.conflict_check_status,
    conflict_check_notes: c.conflict_check_notes || "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try { await api.patch(`/cases/${c.id}`, f); toast.success("Case updated"); onChange(); }
    catch (e) { toast.error("Update failed"); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Section title="Case Summary"><p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{c.summary}</p></Section>
        <Section title="Merit Review"><textarea disabled={!isStaff} rows={4} value={f.merit_review} onChange={set("merit_review")} className="w-full border border-slate-300 p-3 outline-none focus:border-[#0A192F] resize-none disabled:bg-slate-50" data-testid="merit-review" placeholder="RN merit assessment…" /></Section>
        <Section title="Standards of Care"><textarea disabled={!isStaff} rows={4} value={f.standards_of_care} onChange={set("standards_of_care")} className="w-full border border-slate-300 p-3 outline-none focus:border-[#0A192F] resize-none disabled:bg-slate-50" data-testid="standards-of-care" placeholder="Applicable standards, sources, and deviations…" /></Section>
        <Section title="Damages & Injury Analysis"><textarea disabled={!isStaff} rows={4} value={f.damages_analysis} onChange={set("damages_analysis")} className="w-full border border-slate-300 p-3 outline-none focus:border-[#0A192F] resize-none disabled:bg-slate-50" data-testid="damages-analysis" placeholder="Injury mechanism, sequelae, life-care considerations…" /></Section>
      </div>
      <div className="space-y-6">
        <Section title="Case Status">
          <select disabled={!isStaff} value={f.status} onChange={set("status")} className="w-full border border-slate-300 p-2.5 outline-none focus:border-[#0A192F] bg-white disabled:bg-slate-50" data-testid="case-status-select">
            {["intake","conflict_check","active","screening","chronology","review","closed"].map(s => <option key={s} value={s}>{s.replaceAll("_"," ")}</option>)}
          </select>
        </Section>
        <Section title="Conflict Check">
          <select disabled={!isStaff} value={f.conflict_check_status} onChange={set("conflict_check_status")} className="w-full border border-slate-300 p-2.5 outline-none focus:border-[#0A192F] bg-white mb-3 disabled:bg-slate-50" data-testid="conflict-status">
            <option value="pending">Pending</option><option value="cleared">Cleared</option><option value="conflict">Conflict</option>
          </select>
          <textarea disabled={!isStaff} rows={3} value={f.conflict_check_notes} onChange={set("conflict_check_notes")} className="w-full border border-slate-300 p-3 outline-none focus:border-[#0A192F] resize-none disabled:bg-slate-50" placeholder="Clearance notes…" data-testid="conflict-notes" />
        </Section>
        {isStaff && <button onClick={save} className="btn-navy w-full py-3 text-[12px] uppercase tracking-widest" data-testid="save-case">Save Changes</button>}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="card-federal p-6">
      <div className="text-caption text-slate-500 mb-3">{title}</div>
      {children}
    </div>
  );
}

function Chronology({ events, caseId, onAdd }) {
  const [f, setF] = useState({ event_date: "", provider: "", description: "", source: "", citation: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const add = async (e) => {
    e.preventDefault();
    await api.post(`/cases/${caseId}/chronology`, f);
    toast.success("Event added");
    setF({ event_date: "", provider: "", description: "", source: "", citation: "" });
    onAdd();
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={add} className="card-federal p-6 space-y-3 lg:col-span-1" data-testid="chronology-form">
        <div className="text-caption text-slate-500">ADD EVENT</div>
        <input required type="date" value={f.event_date} onChange={set("event_date")} className="w-full border border-slate-300 p-2.5" data-testid="chrono-date" />
        <input placeholder="Provider" value={f.provider} onChange={set("provider")} className="w-full border border-slate-300 p-2.5" data-testid="chrono-provider" />
        <textarea required placeholder="Event description" rows={3} value={f.description} onChange={set("description")} className="w-full border border-slate-300 p-2.5 resize-none" data-testid="chrono-desc" />
        <input placeholder="Source (document / page)" value={f.source} onChange={set("source")} className="w-full border border-slate-300 p-2.5" data-testid="chrono-source" />
        <input placeholder="Citation" value={f.citation} onChange={set("citation")} className="w-full border border-slate-300 p-2.5" data-testid="chrono-citation" />
        <button className="btn-navy w-full py-2.5 text-[12px] uppercase tracking-widest" data-testid="chrono-add">Add Event</button>
      </form>
      <div className="lg:col-span-2 card-federal p-6">
        <div className="text-caption text-slate-500 mb-4">TIMELINE ({events.length})</div>
        <div className="relative pl-6 border-l-2 border-slate-200 space-y-6">
          {events.length === 0 && <div className="text-slate-500 text-sm">No events yet.</div>}
          {events.map((e) => (
            <div key={e.id} className="relative" data-testid={`chrono-event-${e.id}`}>
              <div className="absolute -left-[31px] top-1 w-3 h-3 bg-[#B91C1C] rounded-full border-2 border-white" />
              <div className="font-mono text-[13px] text-[#0A192F] font-semibold">{e.event_date} {e.provider && <span className="text-slate-400">· {e.provider}</span>}</div>
              <div className="text-slate-700 text-sm mt-1">{e.description}</div>
              {(e.source || e.citation) && <div className="text-caption mt-1 text-slate-500">{e.source} {e.citation}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Documents({ docs, caseId, onChange }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/cases/${caseId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      onChange();
    } catch (err) { toast.error("Upload failed"); } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const download = async (d) => {
    const r = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = d.original_filename; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="card-federal p-6">
      <div className="flex justify-between items-center mb-5">
        <div><div className="text-caption text-slate-500">ENCRYPTED VAULT</div><h3 className="font-serif font-bold text-lg text-[#0A192F]">Case Documents</h3></div>
        <label className="btn-navy inline-flex items-center gap-2 px-4 py-2.5 text-[12px] uppercase tracking-widest cursor-pointer" data-testid="btn-upload">
          <Upload className="w-4 h-4" />{busy ? "Uploading…" : "Upload"}
          <input ref={inputRef} type="file" className="hidden" onChange={upload} data-testid="upload-input" />
        </label>
      </div>
      <div className="divide-y divide-slate-100">
        {docs.length === 0 && <div className="text-slate-500 text-sm py-4">No documents yet.</div>}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center justify-between py-3" data-testid={`doc-${d.id}`}>
            <div>
              <div className="text-[#0A192F] font-medium">{d.original_filename}</div>
              <div className="text-caption text-slate-500">{(d.size/1024).toFixed(1)} KB · {d.content_type} · by {d.uploaded_by_name}</div>
            </div>
            <button onClick={() => download(d)} className="text-[12px] uppercase tracking-widest text-[#B91C1C] hover:text-[#991B1B]" data-testid={`download-${d.id}`}>Download</button>
          </div>
        ))}
      </div>
    </div>
  );
}

const AI_KINDS = [
  { k: "summarize", label: "Summarize Record" },
  { k: "chronology_draft", label: "Draft Chronology" },
  { k: "event_extract", label: "Extract Events" },
  { k: "gap_detection", label: "Detect Gaps" },
  { k: "issue_spotting", label: "Spot Issues" },
];

async function streamAiRun(caseId, kind, inputText, onChunk, onDone) {
  const res = await fetch(`${API}/cases/${caseId}/ai/run`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ kind, input_text: inputText }),
  });
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop() || "";
    for (const p of parts) {
      if (p.startsWith("data: ")) onChunk(p.slice(6));
      if (p.startsWith("event: done")) onDone();
    }
  }
}

function AIRunCard({ r, isStaff, onReview }) {
  return (
    <div className="border border-slate-200 p-4" data-testid={`ai-run-${r.id}`}>
      <div className="flex justify-between items-center mb-2">
        <div className="text-caption text-[#0A192F]">{r.kind}</div>
        {r.reviewed ? (
          <span className={`badge-federal ${r.approved ? "!border-[#15803D] !text-[#15803D]" : "!border-[#B91C1C] !text-[#B91C1C]"}`}>{r.approved ? "Approved" : "Rejected"}</span>
        ) : <span className="badge-gold">Pending RN Review</span>}
      </div>
      <div className="text-xs text-slate-500 mb-2 font-mono">{r.created_at?.slice(0,19)} · by {r.created_by_name}</div>
      <div className="text-sm text-slate-700 whitespace-pre-wrap max-h-32 overflow-y-auto">{r.output_text?.slice(0, 500) || "(streaming)"}{r.output_text?.length > 500 ? "…" : ""}</div>
      {isStaff && !r.reviewed && r.status === "complete" && (
        <div className="flex gap-2 mt-3">
          <button onClick={() => onReview(r.id, true)} className="text-[11px] uppercase tracking-widest px-3 py-1.5 bg-[#15803D] text-white inline-flex items-center gap-1" data-testid={`approve-${r.id}`}><Check className="w-3.5 h-3.5" /> Approve</button>
          <button onClick={() => onReview(r.id, false)} className="text-[11px] uppercase tracking-widest px-3 py-1.5 border border-[#B91C1C] text-[#B91C1C] inline-flex items-center gap-1" data-testid={`reject-${r.id}`}><X className="w-3.5 h-3.5" /> Reject</button>
        </div>
      )}
    </div>
  );
}

function AIStudio({ runs, caseId, isStaff, onChange }) {
  const [kind, setKind] = useState("summarize");
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [streaming, setStreaming] = useState(false);

  const run = async () => {
    if (!input.trim()) return toast.error("Paste source text first");
    setStreaming(true); setOutput("");
    try {
      await streamAiRun(caseId, kind, input, (c) => setOutput((o) => o + c), onChange);
    } catch {
      toast.error("AI run failed");
    } finally {
      setStreaming(false);
      onChange();
    }
  };

  const review = async (runId, approved) => {
    await api.post(`/ai/runs/${runId}/review`, { approved, reviewer_notes: approved ? "Approved by RN" : "Rejected by RN" });
    toast.success(approved ? "Approved" : "Rejected");
    onChange();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="card-federal p-6">
        <div className="flex gap-2 items-center mb-3"><Sparkles className="w-4 h-4 text-[#D97706]" /><div className="text-caption text-slate-500">AI STUDIO · CLAUDE SONNET</div></div>
        <div className="flex flex-wrap gap-2 mb-4">
          {AI_KINDS.map((x) => (
            <button key={x.k} onClick={() => setKind(x.k)} data-testid={`ai-kind-${x.k}`}
              className={`text-[11px] uppercase tracking-widest px-3 py-2 border ${kind === x.k ? "border-[#0A192F] bg-[#0A192F] text-white" : "border-slate-300 hover:border-[#0A192F]"}`}>{x.label}</button>
          ))}
        </div>
        <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={10} placeholder="Paste the source medical record text here. All AI outputs require mandatory RN review." className="w-full border border-slate-300 p-3 outline-none focus:border-[#0A192F] resize-none text-sm" data-testid="ai-input" />
        <button disabled={streaming} onClick={run} className="btn-navy w-full mt-3 py-2.5 text-[12px] uppercase tracking-widest disabled:opacity-50" data-testid="ai-run">{streaming ? "Streaming…" : "Run AI Draft"}</button>
        {output && (
          <div className="mt-4 p-4 bg-[#F1F5F9] border-l-4 border-[#B91C1C] text-sm whitespace-pre-wrap font-mono text-slate-700 max-h-64 overflow-y-auto" data-testid="ai-output">{output}</div>
        )}
      </div>
      <div className="card-federal p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-serif font-bold text-lg text-[#0A192F]">AI Run History</h3>
          <span className="badge-crimson"><ShieldCheck className="w-3.5 h-3.5" />RN Review Required</span>
        </div>
        <div className="space-y-3 max-h-[520px] overflow-y-auto">
          {runs.length === 0 && <div className="text-slate-500 text-sm">No runs yet.</div>}
          {runs.map((r) => <AIRunCard key={r.id} r={r} isStaff={isStaff} onReview={review} />)}
        </div>
      </div>
    </div>
  );
}

function Tasks({ tasks, caseId, onChange }) {
  const [f, setF] = useState({ title: "", description: "", due_date: "", assignee: "", priority: "medium" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const add = async (e) => {
    e.preventDefault();
    await api.post(`/cases/${caseId}/tasks`, f);
    setF({ title: "", description: "", due_date: "", assignee: "", priority: "medium" });
    onChange();
  };
  const toggle = async (t) => {
    await api.patch(`/tasks/${t.id}?status=${t.status === "open" ? "done" : "open"}`);
    onChange();
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={add} className="card-federal p-6 space-y-3" data-testid="task-form">
        <div className="text-caption text-slate-500">NEW TASK</div>
        <input required placeholder="Title" value={f.title} onChange={set("title")} className="w-full border border-slate-300 p-2.5" data-testid="task-title" />
        <textarea rows={3} placeholder="Description" value={f.description} onChange={set("description")} className="w-full border border-slate-300 p-2.5 resize-none" data-testid="task-desc" />
        <input type="date" value={f.due_date} onChange={set("due_date")} className="w-full border border-slate-300 p-2.5" data-testid="task-due" />
        <input placeholder="Assignee" value={f.assignee} onChange={set("assignee")} className="w-full border border-slate-300 p-2.5" data-testid="task-assignee" />
        <select value={f.priority} onChange={set("priority")} className="w-full border border-slate-300 p-2.5 bg-white" data-testid="task-priority"><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select>
        <button className="btn-navy w-full py-2.5 text-[12px] uppercase tracking-widest" data-testid="task-add">Add Task</button>
      </form>
      <div className="lg:col-span-2 card-federal p-6">
        <div className="flex justify-between items-center mb-4"><h3 className="font-serif font-bold text-lg text-[#0A192F]"><ClipboardList className="inline w-5 h-5 mr-2 -mt-1" />Tasks & Deadlines</h3></div>
        <div className="divide-y divide-slate-100">
          {tasks.length === 0 && <div className="text-slate-500 text-sm py-4">No tasks yet.</div>}
          {tasks.map((t) => (
            <div key={t.id} className="py-3 flex items-start gap-3" data-testid={`task-${t.id}`}>
              <input type="checkbox" checked={t.status === "done"} onChange={() => toggle(t)} className="mt-1" data-testid={`task-toggle-${t.id}`} />
              <div className="flex-1">
                <div className={`${t.status === "done" ? "line-through text-slate-400" : "text-[#0A192F]"} font-serif`}>{t.title}</div>
                <div className="text-caption text-slate-500">{t.due_date || "—"} · {t.assignee || "unassigned"} · {t.priority}</div>
                {t.description && <div className="text-sm text-slate-600 mt-1">{t.description}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Messages({ msgs, caseId, onChange, me }) {
  const [body, setBody] = useState("");
  const send = async (e) => {
    e.preventDefault(); if (!body.trim()) return;
    await api.post(`/cases/${caseId}/messages`, { body });
    setBody(""); onChange();
  };
  return (
    <div className="card-federal p-6 max-w-3xl">
      <div className="text-caption text-slate-500 mb-4">SECURE MESSAGING · CASE-SCOPED</div>
      <div className="space-y-3 mb-4 max-h-[420px] overflow-y-auto pr-2">
        {msgs.length === 0 && <div className="text-slate-500 text-sm">No messages yet.</div>}
        {msgs.map((m) => (
          <div key={m.id} className={`p-3 border-l-2 ${m.author_id === me.id ? "border-[#B91C1C] bg-[#FEF2F2]" : "border-[#0A192F] bg-slate-50"}`} data-testid={`msg-${m.id}`}>
            <div className="text-caption text-slate-500 mb-1">{m.author_name} · {m.author_role} · {m.created_at?.slice(0,16)}</div>
            <div className="text-slate-800 text-sm whitespace-pre-wrap">{m.body}</div>
          </div>
        ))}
      </div>
      <form onSubmit={send} className="flex gap-2">
        <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write a message…" className="flex-1 border border-slate-300 p-2.5 outline-none focus:border-[#0A192F]" data-testid="msg-input" />
        <button className="btn-navy px-4 py-2.5 inline-flex items-center gap-1.5 text-[12px] uppercase tracking-widest" data-testid="msg-send"><Send className="w-4 h-4" /> Send</button>
      </form>
    </div>
  );
}
